import React, { useState, useEffect } from 'react';
import VobForm from './VobForm';
import axios from 'axios';

const API = 'https://tcio-api.onrender.com';

const STAGES = [
  { key: 'new', label: 'New', color: '#6366f1' },
  { key: 'contacted', label: 'Contacted', color: '#f59e0b' },
  { key: 'vob_pending', label: 'VOB Pending', color: '#f97316' },
  { key: 'vob_complete', label: 'VOB Complete', color: '#3b82f6' },
  { key: 'pre_admit', label: 'Pre-Admit', color: '#8b5cf6' },
  { key: 'admitted', label: 'Admitted', color: '#22c55e' },
  { key: 'lost', label: 'Lost', color: '#ef4444' },
];

const STAGE_COLORS = Object.fromEntries(STAGES.map(s => [s.key, s.color]));
const LOC_OPTIONS = ['DTX', 'RTC', 'PHP', 'IOP'];
// Facility picker is API-fed (scoped server-side) - hardcoded list removed 2026-08-28

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

function fmt(val) { return val || '—'; }

function fmtDOB(dob) {
  if (!dob) return '';
  try { const m = String(dob).match(/^(\d{4}-\d{2}-\d{2})/); if (m) return m[1]; return new Date(dob).toISOString().split('T')[0]; }
  catch { return dob; }
}

// ── LEAD DETAIL PANEL ──────────────────────────────────────────
function LeadDetail({ lead, onClose, onAdmitted, onStageChange }) {
  const [showAdmitModal, setShowAdmitModal] = useState(false);
  const [fullLead, setFullLead] = useState(null);
  const [alumni, setAlumni] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  const [facList, setFacList] = useState([]);
  const [showVobForm, setShowVobForm] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '', last_name: '', phone: '', email: '',
    dob: '', gender: '', source_channel: '', loc_requested: '',
    facility_id: '', notes: '',
    payer_name: '', plan_name: '', member_id: '', group_number: '',
    deductible: '', deductible_met: '', oop_max: '', oop_met: '',
    oon_coverage_pct: '', oon_coverage: '', auth_required: '', mental_health_benefit: '',
  });

  useEffect(() => {
    fetch(`${API}/api/facilities`, { headers: { Authorization: 'Bearer ' + (localStorage.getItem('tcio_token') || '') } })
      .then(r => r.json())
      .then(j => { if (j.ok) setFacList(j.data || []); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API}/api/leads/${lead.id}/full`)
      .then(r => r.json())
      .then(d => {
        if (d.ok) {
          setFullLead(d.data);
          setEditForm({
            first_name: d.data.first_name || lead.first_name || '',
            last_name: d.data.last_name || lead.last_name || '',
            phone: lead.phone || '',
            email: d.data.email || lead.email || '',
            dob: fmtDOB(d.data.dob),
            gender: d.data.gender || '',
            source_channel: d.data.source_channel || '',
            loc_requested: d.data.loc_requested || '',
            facility_id: lead.facility_id || '',
            notes: d.data.notes || lead.notes || '',
            payer_name: d.data.payer_name || '',
            plan_name: d.data.plan_name || '',
            member_id: d.data.member_id || '',
            group_number: d.data.group_number || '',
            deductible: d.data.deductible || '',
            deductible_met: d.data.deductible_met || '',
            oop_max: d.data.oop_max || '',
            oop_met: d.data.oop_met || '',
            oon_coverage_pct: d.data.oon_coverage_pct || '',
            oon_coverage: d.data.oon_coverage != null ? String(d.data.oon_coverage) : '',
            auth_required: d.data.auth_required != null ? String(d.data.auth_required) : '',
            mental_health_benefit: d.data.mental_health_benefit != null ? String(d.data.mental_health_benefit) : '',
          });
        }
      })
      .catch(err => console.error('Failed to fetch full lead:', err));

    const name = `${lead.first_name} ${lead.last_name}`.trim();
    const phone = lead.phone || '';
    const params = new URLSearchParams();
    if (name) params.append('name', name);
    if (phone) params.append('phone', phone);
    fetch(`${API}/api/dazos/lookup?${params}`)
      .then(r => r.json())
      .then(d => { if (d.ok && d.count > 0) setAlumni(d.data); })
      .catch(err => console.error('Alumni lookup failed:', err));
  }, [lead.id]);

  const displayLead = fullLead ? { ...lead, ...fullLead } : lead;

  const handleStageChange = async (newStage) => {
    setUpdating(true);
    try {
      await axios.patch(`${API}/api/leads/${lead.id}/stage`, { stage: newStage });
      onStageChange(lead.id, newStage);
    } catch (err) { alert((err.response && err.response.data && err.response.data.error) || 'Failed to update stage'); }
    setUpdating(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const token = localStorage.getItem('tcio_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Update contact info
      const contactRes = await fetch(`${API}/api/leads/${lead.id}/contact`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: editForm.first_name,
          last_name: editForm.last_name,
          phone: editForm.phone,
          email: editForm.email,
          dob: editForm.dob || null,
          gender: editForm.gender || null,
          source_channel: editForm.source_channel || null,
        }),
      });
      const contactData = await contactRes.json();
      if (!contactRes.ok) {
        setSaveMsg({ type: 'error', text: contactData.error || 'Contact save failed' });
        setSaving(false);
        return;
      }
      const phoneWarning = contactData.warning || null;

      // Update lead info
      await fetch(`${API}/api/leads/${lead.id}/details`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          loc_requested: editForm.loc_requested || null,
          facility_id: editForm.facility_id || null,
          notes: editForm.notes || null,
        }),
      });

      // Update VOB
      await fetch(`${API}/api/leads/${lead.id}/vob`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payer_name: editForm.payer_name || null,
          plan_name: editForm.plan_name || null,
          member_id: editForm.member_id || null,
          group_number: editForm.group_number || null,
          deductible: editForm.deductible || null,
          deductible_met: editForm.deductible_met || null,
          oop_max: editForm.oop_max || null,
          oop_met: editForm.oop_met || null,
          oon_coverage_pct: editForm.oon_coverage_pct || null,
          oon_coverage: editForm.oon_coverage === 'true' ? true : editForm.oon_coverage === 'false' ? false : null,
          auth_required: editForm.auth_required === 'true' ? true : editForm.auth_required === 'false' ? false : null,
          mental_health_benefit: editForm.mental_health_benefit === 'true' ? true : editForm.mental_health_benefit === 'false' ? false : null,
        }),
      });

      setSaveMsg(phoneWarning
        ? { type: 'warning', text: '✓ Saved — ' + phoneWarning }
        : { type: 'success', text: '✓ Saved' });
      setEditMode(false);
      // Refresh full lead data
      const res = await fetch(`${API}/api/leads/${lead.id}/full`);
      const d = await res.json();
      if (d.ok) setFullLead(d.data);
      setTimeout(() => setSaveMsg(null), phoneWarning ? 10000 : 3000);
    } catch (err) {
      setSaveMsg({ type: 'error', text: 'Save failed — try again' });
    }
    setSaving(false);
  };

  const viewVOB = async () => {
    try {
      const res = await axios.get(`${API}/api/leads/${lead.id}/vob-download`);
      window.open(res.data.url, '_blank');
    } catch (err) { alert('No VOB document found.'); }
  };

  const ef = editForm;
  const set = (k, v) => setEditForm(prev => ({ ...prev, [k]: v }));

  return (
    <div style={styles.detailOverlay}>
      <div style={styles.detailPanel}>

        {/* Header */}
        <div style={styles.detailHeader}>
          <div>
            <div style={styles.detailName}>
              {editMode
                ? <div style={{ display: 'flex', gap: '8px' }}>
                    <input value={ef.first_name} onChange={e => set('first_name', e.target.value)} style={styles.editHeaderInput} placeholder="First name" />
                    <input value={ef.last_name} onChange={e => set('last_name', e.target.value)} style={styles.editHeaderInput} placeholder="Last name" />
                  </div>
                : `${lead.first_name} ${lead.last_name}`}
            </div>
            <div style={styles.detailSub}>
              {editMode
                ? <input value={ef.phone} onChange={e => set('phone', e.target.value)} style={{ ...styles.editHeaderInput, width: '160px' }} placeholder="Phone" />
                : `${lead.phone || ''} ${lead.email ? `· ${lead.email}` : ''}`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {saveMsg && (
              <span style={{ fontSize: '12px', color: saveMsg.type === 'success' ? '#22c55e' : saveMsg.type === 'warning' ? '#f59e0b' : '#ef4444', fontWeight: '600' }}>
                {saveMsg.text}
              </span>
            )}
            {editMode ? (
              <>
                <button onClick={handleSave} disabled={saving} style={styles.saveBtn}>
                  {saving ? 'Saving...' : '✓ Save'}
                </button>
                <button onClick={() => setEditMode(false)} style={styles.cancelEditBtn}>Cancel</button>
              </>
            ) : (
              <>
                {lead.pipeline_stage !== 'admitted' && (
                  <button onClick={() => setShowAdmitModal(true)} style={styles.admitBtn}>🏥 Admit</button>
                )}
                <button onClick={() => setEditMode(true)} style={styles.editBtn}>✏ Edit</button>
              </>
            )}
            <button onClick={onClose} style={styles.closeBtn}>✕</button>
          </div>
        </div>

        {/* Alumni Banner */}
        {alumni.length > 0 && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderLeft: '4px solid #f59e0b', padding: '12px 24px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
              ⚠ Alumni / Prior Patient — {alumni.length} record{alumni.length > 1 ? 's' : ''} found
            </div>
            {alumni.map((a, i) => (
              <div key={i} style={{ fontSize: '13px', color: '#78350f', marginBottom: '4px' }}>
                <strong>{a.facility_name}</strong> · {a.admission_date ? new Date(a.admission_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'No admit date'} · {a.insurance_company || 'Unknown payer'} · {a.sales_stage}{a.los_days > 0 ? ` · ${a.los_days} days` : ''}
              </div>
            ))}
          </div>
        )}

        {/* Stage */}
        <div style={styles.detailSection}>
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Stage</span>
            <select value={lead.pipeline_stage} onChange={e => handleStageChange(e.target.value)}
              disabled={updating} style={styles.stageSelect}>
              {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <button onClick={() => setShowVobForm(true)} style={styles.vobBtnSmall}>🛡 VOB details</button>
            {showVobForm && <VobForm user={null} leadId={displayLead.id} onClose={() => setShowVobForm(false)} />}
            {displayLead.has_vob && (
              <button onClick={viewVOB} style={styles.vobBtnSmall}>📄 View VOB</button>
            )}
          </div>
        </div>

        {/* Patient Info */}
        <div style={styles.detailSection}>
          <div style={styles.sectionTitle}>Patient Info</div>
          <div style={styles.detailGrid}>
            {editMode ? (
              <>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Email</span>
                  <input value={ef.email} onChange={e => set('email', e.target.value)} style={styles.editInput} placeholder="Email" />
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>DOB</span>
                  <input type="date" value={ef.dob} onChange={e => set('dob', e.target.value)} style={styles.editInput} />
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Gender</span>
                  <select value={ef.gender} onChange={e => set('gender', e.target.value)} style={styles.editInput}>
                    <option value="">Select...</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>LOC Requested</span>
                  <select value={ef.loc_requested} onChange={e => set('loc_requested', e.target.value)} style={styles.editInput}>
                    <option value="">Select...</option>
                    {LOC_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Source</span>
                  <input value={ef.source_channel} onChange={e => set('source_channel', e.target.value)} style={styles.editInput} placeholder="Source" />
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Facility</span>
                  <select value={ef.facility_id} onChange={e => set('facility_id', e.target.value)} style={styles.editInput}>
                    <option value="">Select...</option>
                    {facList.map(f => <option key={f.id} value={f.id}>{f.org_name}</option>)}
                  </select>
                </div>
              </>
            ) : (
              <>
                <div style={styles.detailItem}><span style={styles.detailLabel}>DOB</span><span style={styles.detailValue}>{fmt(displayLead.dob ? (() => { const s = String(displayLead.dob).slice(0,10); const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/); return (m ? new Date(+m[1], +m[2]-1, +m[3]) : new Date(displayLead.dob)).toLocaleDateString('en-US'); })() : null)}</span></div>
                <div style={styles.detailItem}><span style={styles.detailLabel}>Gender</span><span style={styles.detailValue}>{fmt(displayLead.gender)}</span></div>
                <div style={styles.detailItem}><span style={styles.detailLabel}>LOC Requested</span><span style={styles.detailValue}>{fmt(displayLead.loc_requested)}</span></div>
                <div style={styles.detailItem}><span style={styles.detailLabel}>Source</span><span style={styles.detailValue}>{fmt(displayLead.source_channel)}</span></div>
                <div style={styles.detailItem}><span style={styles.detailLabel}>Facility</span><span style={styles.detailValue}>{fmt(lead.facility_name)}</span></div>
                <div style={styles.detailItem}><span style={styles.detailLabel}>Created</span><span style={styles.detailValue}>{timeAgo(lead.created_at)}</span></div>
              </>
            )}
          </div>
          {editMode ? (
            <div style={{ marginTop: '12px' }}>
              <span style={styles.detailLabel}>Notes</span>
              <textarea value={ef.notes} onChange={e => set('notes', e.target.value)}
                style={{ ...styles.editInput, height: '70px', resize: 'vertical', width: '100%', boxSizing: 'border-box', marginTop: '4px' }} placeholder="Notes..." />
            </div>
          ) : (
            displayLead.notes && <div style={styles.notesBox}>{displayLead.notes}</div>
          )}
        </div>

        {/* Insurance / VOB */}
        <div style={styles.detailSection}>
          <div style={styles.sectionTitle}>Insurance / VOB</div>
          {!fullLead && !editMode ? (
            <div style={{ color: '#999', fontSize: '13px' }}>Loading VOB data...</div>
          ) : (
            <div style={styles.detailGrid}>
              {editMode ? (
                <>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Payer / Insurance</span>
                    <input value={ef.payer_name} onChange={e => set('payer_name', e.target.value)} style={styles.editInput} placeholder="e.g. Anthem Blue Cross" />
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Plan Name</span>
                    <input value={ef.plan_name} onChange={e => set('plan_name', e.target.value)} style={styles.editInput} placeholder="Plan name" />
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Member ID</span>
                    <input value={ef.member_id} onChange={e => set('member_id', e.target.value)} style={styles.editInput} placeholder="Member ID" />
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Group #</span>
                    <input value={ef.group_number} onChange={e => set('group_number', e.target.value)} style={styles.editInput} placeholder="Group number" />
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Deductible</span>
                    <input type="number" value={ef.deductible} onChange={e => set('deductible', e.target.value)} style={styles.editInput} placeholder="0" />
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Deductible Met</span>
                    <input type="number" value={ef.deductible_met} onChange={e => set('deductible_met', e.target.value)} style={styles.editInput} placeholder="0" />
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>OOP Max</span>
                    <input type="number" value={ef.oop_max} onChange={e => set('oop_max', e.target.value)} style={styles.editInput} placeholder="0" />
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>OOP Met</span>
                    <input type="number" value={ef.oop_met} onChange={e => set('oop_met', e.target.value)} style={styles.editInput} placeholder="0" />
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>OON Coverage %</span>
                    <input type="number" value={ef.oon_coverage_pct} onChange={e => set('oon_coverage_pct', e.target.value)} style={styles.editInput} placeholder="0-100" />
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>OON Coverage</span>
                    <select value={ef.oon_coverage} onChange={e => set('oon_coverage', e.target.value)} style={styles.editInput}>
                      <option value="">Unknown</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Auth Required</span>
                    <select value={ef.auth_required} onChange={e => set('auth_required', e.target.value)} style={styles.editInput}>
                      <option value="">Unknown</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Mental Health Benefit</span>
                    <select value={ef.mental_health_benefit} onChange={e => set('mental_health_benefit', e.target.value)} style={styles.editInput}>
                      <option value="">Unknown</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>Payer</span><span style={styles.detailValue}>{fmt(displayLead.payer_name)}</span></div>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>Plan</span><span style={styles.detailValue}>{fmt(displayLead.plan_name)}</span></div>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>Member ID</span><span style={styles.detailValue}>{fmt(displayLead.member_id)}</span></div>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>Group #</span><span style={styles.detailValue}>{fmt(displayLead.group_number)}</span></div>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>Deductible</span><span style={styles.detailValue}>{displayLead.deductible ? `$${displayLead.deductible}` : '—'}</span></div>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>Deductible Met</span><span style={styles.detailValue}>{displayLead.deductible_met ? `$${displayLead.deductible_met}` : '—'}</span></div>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>OOP Max</span><span style={styles.detailValue}>{displayLead.oop_max ? `$${displayLead.oop_max}` : '—'}</span></div>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>OOP Met</span><span style={styles.detailValue}>{displayLead.oop_met ? `$${displayLead.oop_met}` : '—'}</span></div>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>OON Coverage</span><span style={styles.detailValue}>{displayLead.oon_coverage_pct ? `${displayLead.oon_coverage_pct}%` : '—'}</span></div>
                  <div style={styles.detailItem}><span style={styles.detailLabel}>Auth Required</span><span style={styles.detailValue}>{displayLead.auth_required === true ? 'Yes' : displayLead.auth_required === false ? 'No' : '—'}</span></div>
                </>
              )}
            </div>
          )}
        </div>

        {showAdmitModal && (
          <AdmitModal
            lead={displayLead}
            onClose={() => setShowAdmitModal(false)}
            onAdmitted={() => {
              setShowAdmitModal(false);
              onAdmitted(lead.id);
              onClose();
            }}
          />
        )}
      </div>
    </div>
  );
}

// ── ADMIT MODAL ────────────────────────────────────────────────
function AdmitModal({ lead, onClose, onAdmitted }) {
  const [beds, setBeds] = useState([]);
  const [selectedBed, setSelectedBed] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingVOB, setLoadingVOB] = useState(true);
  const [form, setForm] = useState({
    patient_name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim(),
    gender: lead.gender || '',
    dob: fmtDOB(lead.dob),
    admit_date: (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })(),
    primary_payer: lead.payer_name || '',
    loc: lead.loc_requested || '',
    member_id: lead.member_id || '',
    group_num: lead.group_number || '',
    mkt_source: lead.source_channel || '',
    therapist: '',
    case_manager: '',
    coordinator: '',
    auth_num: '',
    auth_thru: '',
    expected_dc: '',
    policy_id: lead.policy_id || '',
    notes: lead.notes || '',
  });

  const FACILITY_ID = lead.facility_id || ''; // beds + admit follow the lead's own facility (zero-hardcode)

  useEffect(() => {
    fetch(`${API}/api/leads/${lead.id}/full`)
      .then(r => r.json())
      .then(d => {
        if (d.ok && d.data) {
          const v = d.data;
          setForm(prev => ({
            ...prev,
            patient_name: `${v.first_name || ''} ${v.last_name || ''}`.trim() || prev.patient_name,
            gender: v.gender || prev.gender,
            dob: fmtDOB(v.dob) || prev.dob,
            primary_payer: v.payer_name || prev.primary_payer,
            loc: v.loc_requested || prev.loc,
            member_id: v.member_id || prev.member_id,
            group_num: v.group_number || prev.group_num,
            mkt_source: v.source_channel || prev.mkt_source,
            policy_id: v.policy_id || prev.policy_id,
            notes: v.notes || prev.notes,
          }));
        }
        setLoadingVOB(false);
      })
      .catch(() => setLoadingVOB(false));

    if (FACILITY_ID) fetch(`${API}/api/beds/${FACILITY_ID}`)
      .then(r => r.json())
      .then(d => { if (d.ok) setBeds(d.data.filter(b => !b.patient_name)); });
  }, [lead.id]);

  const bedsByHouse = beds.reduce((acc, b) => {
    if (!acc[b.house_name]) acc[b.house_name] = [];
    acc[b.house_name].push(b);
    return acc;
  }, {});

  const handleAdmit = async () => {
    if (!FACILITY_ID) return alert('This lead has no facility assigned. Set the facility on the lead first, then admit.');
    if (!selectedBed) return alert('Please select a bed.');
    setSaving(true);
    try {
      await axios.post(`${API}/api/admit`, { bed_id: selectedBed.bed_code, facility_id: FACILITY_ID, ...form });
      await axios.patch(`${API}/api/leads/${lead.id}/stage`, { stage: 'admitted' });
      onAdmitted();
    } catch (err) {
      alert(err.response?.data?.error || 'Admit failed');
    }
    setSaving(false);
  };

  const LOC_COLORS = { DTX: '#f59e0b', RTC: '#3b82f6', PHP: '#10b981', IOP: '#8b5cf6' };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.admitModalBox}>
        <div style={styles.admitModalHeader}>
          <div>
            <div style={styles.admitModalTitle}>Admit {form.patient_name}</div>
            {loadingVOB
              ? <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginTop: '3px' }}>Loading VOB data...</div>
              : <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginTop: '3px' }}>✓ Pre-filled from lead record</div>}
          </div>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>
        <div style={styles.admitModalBody}>
          <div style={styles.bedPickerCol}>
            <div style={styles.admitSectionTitle}>Select a Bed ({beds.length} open)</div>
            <div style={styles.bedList}>
              {Object.entries(bedsByHouse).map(([house, houseBeds]) => (
                <div key={house}>
                  <div style={styles.houseLabel}>{house}</div>
                  {houseBeds.map(b => (
                    <div key={b.id} onClick={() => setSelectedBed(b)}
                      style={{ ...styles.bedOption, ...(selectedBed?.id === b.id ? styles.bedOptionSelected : {}) }}>
                      <span style={styles.bedCode}>{b.bed_code}</span>
                      <span style={{ ...styles.locPill, background: (LOC_COLORS[b.loc] || '#999') + '22', color: LOC_COLORS[b.loc] || '#999' }}>{b.loc}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div style={styles.admitFormCol}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={styles.admitSectionTitle}>Admit Details</div>
              <div style={{ fontSize: '11px', color: '#22c55e', fontWeight: '600' }}>🟢 Green = pre-filled from VOB</div>
            </div>
            <div style={styles.formGrid}>
              {[
                { label: 'Patient Name', key: 'patient_name', prefilled: true },
                { label: 'Admit Date', key: 'admit_date', type: 'date' },
                { label: 'Payer', key: 'primary_payer', prefilled: true },
                { label: 'LOC', key: 'loc', type: 'select', options: LOC_OPTIONS, prefilled: true },
                { label: 'Member ID', key: 'member_id', prefilled: true },
                { label: 'Group #', key: 'group_num', prefilled: true },
                { label: 'Gender', key: 'gender', type: 'select', options: ['M', 'F'], prefilled: true },
                { label: 'DOB', key: 'dob', type: 'date', prefilled: true },
                { label: 'Mkt Source', key: 'mkt_source', prefilled: true },
                { label: 'Therapist', key: 'therapist' },
                { label: 'Case Manager', key: 'case_manager' },
                { label: 'Coordinator', key: 'coordinator' },
                { label: 'Auth Number', key: 'auth_num' },
                { label: 'Auth Thru', key: 'auth_thru', type: 'date' },
                { label: 'Expected DC', key: 'expected_dc', type: 'date' },
              ].map(f => (
                <div key={f.key} style={styles.formField}>
                  <label style={{ ...styles.formLabel, color: f.prefilled && form[f.key] ? '#166534' : '#666' }}>
                    {f.prefilled && form[f.key] ? '✓ ' : ''}{f.label}
                  </label>
                  {f.type === 'select' ? (
                    <select value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                      style={{ ...styles.formInput, borderColor: f.prefilled && form[f.key] ? '#22c55e' : '#d1d5db', background: f.prefilled && form[f.key] ? '#f0fdf4' : 'white' }}>
                      <option value="">Select...</option>
                      {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={f.type || 'text'} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                      style={{ ...styles.formInput, borderColor: f.prefilled && form[f.key] ? '#22c55e' : '#d1d5db', background: f.prefilled && form[f.key] ? '#f0fdf4' : 'white' }} />
                  )}
                </div>
              ))}
              <div style={{ ...styles.formField, gridColumn: '1 / -1' }}>
                <label style={styles.formLabel}>Notes</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  style={{ ...styles.formInput, height: '60px', resize: 'vertical' }} />
              </div>
            </div>
            {selectedBed && (
              <div style={styles.selectedBedBanner}>
                Admitting to: <strong>{selectedBed.bed_code}</strong> ({selectedBed.house_name} · {selectedBed.loc})
              </div>
            )}
            <button onClick={handleAdmit} disabled={saving || !selectedBed || loadingVOB}
              style={{ ...styles.admitSubmitBtn, opacity: saving || !selectedBed || loadingVOB ? 0.5 : 1 }}>
              {saving ? 'Admitting...' : loadingVOB ? 'Loading...' : '✓ Confirm Admit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MAIN PIPELINE ──────────────────────────────────────────────
export default function LeadPipeline() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [updating, setUpdating] = useState(null);

  useEffect(() => { fetchLeads(); }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/leads`);
      setLeads(res.data.data || []);
    } catch (err) { console.error('Failed to fetch leads:', err); }
    setLoading(false);
  };

  const viewVOB = async (leadId, e) => {
    e.stopPropagation();
    try {
      const res = await axios.get(`${API}/api/leads/${leadId}/vob-download`);
      window.open(res.data.url, '_blank');
    } catch (err) { alert('No VOB document found for this lead.'); }
  };

  const updateStage = async (leadId, newStage, e) => {
    e.stopPropagation();
    setUpdating(leadId);
    try {
      await axios.patch(`${API}/api/leads/${leadId}/stage`, { stage: newStage });
      setLeads(leads.map(l => l.id === leadId ? { ...l, pipeline_stage: newStage } : l));
    } catch (err) { alert((err.response && err.response.data && err.response.data.error) || 'Failed to update stage'); }
    setUpdating(null);
  };

  const handleAdmitted = (leadId) => {
    setLeads(leads.map(l => l.id === leadId ? { ...l, pipeline_stage: 'admitted' } : l));
    setSelectedLead(null);
  };

  const handleStageChange = (leadId, newStage) => {
    setLeads(leads.map(l => l.id === leadId ? { ...l, pipeline_stage: newStage } : l));
  };

  const activeLeads = leads.filter(l => l.pipeline_stage !== 'admitted');
  const filtered = leads.filter(l => {
    const matchStage = filter === 'all' ? l.pipeline_stage !== 'admitted' : l.pipeline_stage === filter;
    const matchSearch = !search ||
      `${l.first_name} ${l.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
      (l.phone || '').includes(search);
    return matchStage && matchSearch;
  });

  const countByStage = (stage) => leads.filter(l => l.pipeline_stage === stage).length;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Lead Pipeline</h2>
          <p style={styles.subtitle}>{activeLeads.length} active leads</p>
        </div>
        <button onClick={fetchLeads} style={styles.refreshBtn}>↻ Refresh</button>
      </div>
      <div style={styles.tabs}>
        <button onClick={() => setFilter('all')} style={{ ...styles.tab, ...(filter === 'all' ? styles.tabActive : {}) }}>
          Active ({activeLeads.length})
        </button>
        {STAGES.map(s => (
          <button key={s.key} onClick={() => setFilter(s.key)}
            style={{ ...styles.tab, ...(filter === s.key ? { ...styles.tabActive, borderBottomColor: s.color, color: s.color } : {}) }}>
            {s.label} ({countByStage(s.key)})
          </button>
        ))}
      </div>
      <div style={styles.searchBar}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or phone..." style={styles.searchInput} />
        {search && <button onClick={() => setSearch('')} style={styles.clearBtn}>✕</button>}
      </div>
      {loading ? (
        <div style={styles.empty}>Loading leads...</div>
      ) : filtered.length === 0 ? (
        <div style={styles.empty}>No leads found.</div>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Phone</th>
                <th style={styles.th}>Payer</th>
                <th style={styles.th}>LOC</th>
                <th style={styles.th}>Stage</th>
                <th style={styles.th}>Created</th>
                <th style={styles.th}>Move To</th>
                <th style={styles.th}>VOB</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(lead => (
                <tr key={lead.id} style={{ ...styles.row, cursor: 'pointer' }} onClick={() => setSelectedLead(lead)}>
                  <td style={styles.td}>
                    <div style={styles.name}>{lead.first_name} {lead.last_name}</div>
                    {lead.email && <div style={styles.email}>{lead.email}</div>}
                  </td>
                  <td style={styles.td}>{lead.phone || '—'}</td>
                  <td style={styles.td}>{lead.payer_name || '—'}</td>
                  <td style={styles.td}>
                    {lead.loc_requested ? <span style={styles.loc}>{lead.loc_requested}</span> : '—'}
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.stageBadge, backgroundColor: STAGE_COLORS[lead.pipeline_stage] + '20', color: STAGE_COLORS[lead.pipeline_stage], borderColor: STAGE_COLORS[lead.pipeline_stage] }}>
                      {STAGES.find(s => s.key === lead.pipeline_stage)?.label || lead.pipeline_stage}
                    </span>
                  </td>
                  <td style={styles.td}>{timeAgo(lead.created_at)}</td>
                  <td style={styles.td} onClick={e => e.stopPropagation()}>
                    <select disabled={updating === lead.id} value={lead.pipeline_stage}
                      onChange={e => updateStage(lead.id, e.target.value, e)} style={styles.stageSelect}>
                      {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  </td>
                  <td style={styles.td} onClick={e => e.stopPropagation()}>
                    {lead.has_vob ? <button onClick={e => viewVOB(lead.id, e)} style={styles.vobBtn}>📄 View</button> : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selectedLead && (
        <LeadDetail
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onAdmitted={handleAdmitted}
          onStageChange={handleStageChange}
        />
      )}
    </div>
  );
}

const styles = {
  container: { padding: '32px', maxWidth: '1200px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { margin: '0 0 4px', fontSize: '24px', color: '#336090' },
  subtitle: { margin: 0, color: '#666', fontSize: '14px' },
  refreshBtn: { backgroundColor: '#336090', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  tabs: { display: 'flex', gap: '4px', marginBottom: '16px', flexWrap: 'wrap', borderBottom: '2px solid #e8f0f7' },
  tab: { padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', color: '#666', borderBottom: '2px solid transparent', marginBottom: '-2px' },
  tabActive: { color: '#336090', borderBottomColor: '#336090', fontWeight: 'bold' },
  searchBar: { position: 'relative', marginBottom: '16px' },
  searchInput: { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' },
  clearBtn: { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: '#999', fontSize: '16px' },
  tableWrap: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { backgroundColor: '#f8fafc' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #e8f0f7' },
  row: { borderBottom: '1px solid #f0f4f8' },
  td: { padding: '14px 16px', fontSize: '14px', color: '#374151', verticalAlign: 'middle' },
  name: { fontWeight: '600', color: '#336090' },
  email: { fontSize: '12px', color: '#888', marginTop: '2px' },
  loc: { backgroundColor: '#e8f0f7', color: '#336090', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
  stageBadge: { padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', border: '1px solid' },
  stageSelect: { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' },
  empty: { textAlign: 'center', padding: '60px', color: '#999', backgroundColor: 'white', borderRadius: '12px' },
  vobBtn: { backgroundColor: '#336090', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' },
  detailOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' },
  detailPanel: { background: 'white', width: '520px', height: '100%', overflowY: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column' },
  detailHeader: { padding: '20px 24px', borderBottom: '1px solid #e8f0f7', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', background: '#336090', color: 'white', position: 'sticky', top: 0, zIndex: 10 },
  detailName: { fontSize: '20px', fontWeight: '700', color: 'white' },
  detailSub: { fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' },
  detailSection: { padding: '16px 24px', borderBottom: '1px solid #f0f4f8' },
  sectionTitle: { fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#999', marginBottom: '12px' },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  detailItem: { display: 'flex', flexDirection: 'column', gap: '2px' },
  detailLabel: { fontSize: '11px', color: '#999', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em' },
  detailValue: { fontSize: '14px', color: '#1a2a3a', fontWeight: '500' },
  detailRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  notesBox: { marginTop: '10px', padding: '10px 12px', background: '#f8fafc', borderRadius: '6px', fontSize: '13px', color: '#444', lineHeight: 1.5 },
  editInput: { padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box', background: '#fffbeb' },
  editHeaderInput: { padding: '4px 8px', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '5px', fontSize: '16px', background: 'rgba(255,255,255,0.15)', color: 'white', width: '130px' },
  editBtn: { backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  saveBtn: { backgroundColor: '#22c55e', color: 'white', border: 'none', padding: '7px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' },
  cancelEditBtn: { backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  admitBtn: { backgroundColor: '#22c55e', color: 'white', border: 'none', padding: '7px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  closeBtn: { background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  vobBtnSmall: { backgroundColor: '#e8f0f7', color: '#336090', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  admitModalBox: { background: 'white', borderRadius: '16px', width: '100%', maxWidth: '900px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  admitModalHeader: { padding: '20px 24px', borderBottom: '1px solid #e8f0f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#336090' },
  admitModalTitle: { fontSize: '18px', fontWeight: '700', color: 'white' },
  admitModalBody: { display: 'flex', flex: 1, overflow: 'hidden' },
  bedPickerCol: { width: '260px', borderRight: '1px solid #e8f0f7', overflowY: 'auto', padding: '16px', flexShrink: 0 },
  admitFormCol: { flex: 1, overflowY: 'auto', padding: '16px 24px' },
  admitSectionTitle: { fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#999', marginBottom: '12px' },
  bedList: { display: 'flex', flexDirection: 'column', gap: '4px' },
  houseLabel: { fontSize: '10px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: '12px', marginBottom: '4px' },
  bedOption: { padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e8f0f7', background: '#fafafa' },
  bedOptionSelected: { background: '#e8f4ff', borderColor: '#336090' },
  bedCode: { fontSize: '13px', fontWeight: '600', color: '#1a2a3a' },
  locPill: { fontSize: '10px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  formField: { display: 'flex', flexDirection: 'column', gap: '4px' },
  formLabel: { fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em' },
  formInput: { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' },
  selectedBedBanner: { marginTop: '16px', padding: '10px 14px', background: '#e8f4ff', borderRadius: '8px', fontSize: '13px', color: '#336090', border: '1px solid #c0d8f0' },
  admitSubmitBtn: { marginTop: '16px', width: '100%', padding: '12px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },
};